import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button, Input, Select, notification,
} from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { blockApi, recommendationApi } from 'farmx-api';
import { actions, selectors } from 'farmx-redux-core';
import {
  compareByName,
  getPercentForFraction,
  renderTableWithoutHeader,
} from '../../../helper/common';
import { TOOLTIP_TITLES } from '../../../helper/tooltipHelper';
import './IrrigationRecommendationTuning.css';

const {
  loadNewBlockDetail,
  loadRecommendationForBlock,
  updateNewBlockItem,
} = actions;

const { selectNewBlockById } = selectors;

const FIELD_CAPACITY = 'field_capacity';
const REFILL_POINT = 'refill_point';
const SOIL_SENSOR_TYPES = ['aquacheck_soil', 'pixl_soil'];
const NUMBER_INPUT_STYLE = { width: 90, textAlign: 'right' };
const TARGET_SELECT_STYLE = { width: 150, textAlign: 'left' };
const ACTION_ROW_STYLE = { marginTop: 8 };
const CANCEL_BUTTON_STYLE = { marginRight: 8 };
const TUNING_TOOLTIP = TOOLTIP_TITLES?.tuning || {};

function isUnset(value) {
  return value === undefined || value === null;
}

export function getInitialValues(block, refillPointRootzone) {
  const refillPoint = isUnset(refillPointRootzone)
    ? undefined
    : getPercentForFraction(refillPointRootzone);

  return {
    krdi: block?.krdi ?? 1,
    irrigationTargetPoint: block?.irrigation_target_point
      || block?.irrigationTargetPoint
      || FIELD_CAPACITY,
    refillPoint: refillPoint
      ?? block?.refill_point
      ?? block?.refillPoint
      ?? undefined,
  };
}

function normalizeOptionalNumber(value) {
  if (isUnset(value) || value === '') return undefined;
  return Number(value);
}

function getErrorMessage(error, fallbackMessage) {
  return error?.response?.data?.detail
    || error?.response?.data?.message
    || error?.response?.data?.error
    || error?.message
    || fallbackMessage;
}

function getValidationMessage(values, t) {
  const krdi = Number(values.krdi);
  if (values.krdi === '' || Number.isNaN(krdi) || krdi < 0 || krdi > 1) {
    return t('KRDI must be between 0 and 1');
  }

  if (values.irrigationTargetPoint === REFILL_POINT) {
    const refillPoint = normalizeOptionalNumber(values.refillPoint);
    if (refillPoint === undefined || Number.isNaN(refillPoint)) {
      return t('Refill Point is required');
    }
    if (refillPoint < 0 || refillPoint > 100) {
      return t('Refill Point must be between 0 and 100');
    }
  }

  return undefined;
}

export function hasFormChanges(values, initialValues) {
  const targetChanged = values.irrigationTargetPoint !== initialValues.irrigationTargetPoint;
  const refillChanged = values.irrigationTargetPoint === REFILL_POINT
    && normalizeOptionalNumber(values.refillPoint)
      !== normalizeOptionalNumber(initialValues.refillPoint);

  return Number(values.krdi) !== Number(initialValues.krdi)
    || targetChanged
    || refillChanged;
}

function mergeFormValues(values, initialValues) {
  return {
    krdi: values.krdi === undefined ? initialValues.krdi : values.krdi,
    irrigationTargetPoint: values.irrigationTargetPoint === undefined
      ? initialValues.irrigationTargetPoint
      : values.irrigationTargetPoint,
    refillPoint: values.refillPoint === undefined ? initialValues.refillPoint : values.refillPoint,
  };
}

function getSoilSensors(sensorsByType) {
  return SOIL_SENSOR_TYPES
    .reduce((sensors, type) => sensors.concat(sensorsByType?.[type] || []), [])
    .sort(compareByName);
}

export async function saveTuningValues({
  blockId,
  values,
  initialValues,
  blockApiClient,
  recommendationApiClient,
  dispatch,
  loadBlockDetailAction,
  loadRecommendationForBlockAction,
  updateBlockItemAction,
}) {
  const krdiChanged = Number(values.krdi) !== Number(initialValues.krdi);
  const targetChanged = values.irrigationTargetPoint !== initialValues.irrigationTargetPoint;
  const refillChanged = values.irrigationTargetPoint === REFILL_POINT
    && normalizeOptionalNumber(values.refillPoint)
    !== normalizeOptionalNumber(initialValues.refillPoint);

  if (!krdiChanged && !targetChanged && !refillChanged) {
    return false;
  }

  if (krdiChanged) {
    await blockApiClient.updateKrdiForBlock(blockId, { krdi: Number(values.krdi) });
  }

  if (targetChanged || refillChanged) {
    const payload = {
      irrigation_target_point: values.irrigationTargetPoint,
    };
    if (values.irrigationTargetPoint === REFILL_POINT) {
      const refillPoint = normalizeOptionalNumber(values.refillPoint);
      if (refillPoint === undefined || Number.isNaN(refillPoint)) {
        throw new Error('Refill Point is required');
      }
      payload.refill_point = refillPoint;
    }
    await blockApiClient.updateIrrigationTargetPointForBlock(blockId, payload);
  }

  const refreshStatusResponse = await recommendationApiClient
    .retrieveRecommendationMetadataRefreshStatus({ blockId });
  const { blockId: refreshBlockId, recommendationRefresh } = refreshStatusResponse?.data || {};
  if (refreshBlockId && recommendationRefresh) {
    dispatch(updateBlockItemAction({
      id: refreshBlockId,
      changes: {
        recommendationRefresh,
      },
    }));
  }

  await Promise.all([
    dispatch(loadBlockDetailAction(blockId)),
    dispatch(loadRecommendationForBlockAction(blockId)),
  ]);

  return true;
}

export function IrrigationRecommendationTuning({ blockId }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const block = useSelector((state) => selectNewBlockById(state, Number(blockId)));
  const sensorsByType = useSelector((state) => selectors.selectAllSensorsForBlockIdsByType(state, {
    blockIds: [Number(blockId)],
  }));
  const [soilSensor] = useMemo(() => getSoilSensors(sensorsByType), [sensorsByType]);
  const sensorStatus = useSelector((state) => selectors
    .selectSensorStatus(state, soilSensor?.type, soilSensor?.identifier)) || {};
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [values, setValues] = useState(() => getInitialValues());
  const [initialValues, setInitialValues] = useState(() => getInitialValues());

  const latestInitialValues = useMemo(
    () => getInitialValues(block, sensorStatus.refillPointRootzone),
    [block, sensorStatus.refillPointRootzone],
  );
  const selectedTargetPoint = values.irrigationTargetPoint;

  useEffect(() => {
    setInitialValues(latestInitialValues);
    setValues(latestInitialValues);
    setHasChanges(false);
    setIsEditing(false);
  }, [latestInitialValues]);

  const updateValues = useCallback((changedValues) => {
    setValues((currentValues) => {
      const nextValues = mergeFormValues({
        ...currentValues,
        ...changedValues,
      }, initialValues);

      setHasChanges(hasFormChanges(nextValues, initialValues));
      return nextValues;
    });
  }, [initialValues]);

  const handleKrdiChange = useCallback((event) => {
    updateValues({ krdi: event.target.value });
  }, [updateValues]);

  const handleTargetPointChange = useCallback((value) => {
    updateValues({ irrigationTargetPoint: value });
  }, [updateValues]);

  const handleRefillPointChange = useCallback((event) => {
    updateValues({ refillPoint: event.target.value });
  }, [updateValues]);

  const handleEditClick = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleCancelClick = useCallback(() => {
    setValues(initialValues);
    setHasChanges(false);
    setIsEditing(false);
  }, [initialValues]);

  async function handleSave(event) {
    event.preventDefault();
    const validationMessage = getValidationMessage(values, t);
    if (validationMessage) {
      notification.error({ message: validationMessage });
      return;
    }

    setIsSaving(true);
    try {
      const didSave = await saveTuningValues({
        blockId,
        values,
        initialValues,
        blockApiClient: blockApi,
        recommendationApiClient: recommendationApi,
        dispatch,
        loadBlockDetailAction: loadNewBlockDetail,
        loadRecommendationForBlockAction: loadRecommendationForBlock,
        updateBlockItemAction: updateNewBlockItem,
      });
      if (didSave) {
        const savedValues = mergeFormValues(values, initialValues);
        setInitialValues(savedValues);
        setValues(savedValues);
        notification.success({
          message: t('Tuning updated successfully'),
        });
        setIsEditing(false);
      }
      setHasChanges(false);
    } catch (error) {
      notification.error({
        message: getErrorMessage(error, t('Failed to update tuning')),
      });
    } finally {
      setIsSaving(false);
    }
  }

  const tuningRows = useMemo(() => [
    {
      label: t('KRDI'),
      value: isEditing ? (
        <Input
          type="number"
          size="small"
          min="0"
          step="0.05"
          style={NUMBER_INPUT_STYLE}
          aria-label={t('KRDI')}
          value={values.krdi}
          className="irrigation-tuning-input"
          disabled={!isEditing || isSaving}
          onChange={handleKrdiChange}
        />
      ) : (
        <span className="irrigation-tuning-readonly-value">{values.krdi}</span>
      ),
      title: TUNING_TOOLTIP?.krdi?.title,
    },
    {
      label: t('Target Point'),
      value: isEditing ? (
        <Select
          size="small"
          style={TARGET_SELECT_STYLE}
          aria-label={t('Target Point')}
          value={values.irrigationTargetPoint}
          className="irrigation-tuning-select"
          disabled={!isEditing || isSaving}
          onChange={handleTargetPointChange}
        >
          <Select.Option value={FIELD_CAPACITY}>{t('Field Capacity')}</Select.Option>
          <Select.Option value={REFILL_POINT}>{t('Refill Point')}</Select.Option>
        </Select>
      ) : (
        <span className="irrigation-tuning-readonly-value">
          {t(selectedTargetPoint === REFILL_POINT ? 'Refill Point' : 'Field Capacity')}
        </span>
      ),
      title: TUNING_TOOLTIP?.targetPoint?.title,
    },
    ...(selectedTargetPoint === REFILL_POINT ? [{
      label: t('Refill Point %'),
      value: isEditing ? (
        <Input
          type="number"
          size="small"
          min="0"
          max="100"
          step="any"
          style={NUMBER_INPUT_STYLE}
          aria-label={t('Refill Point %')}
          value={values.refillPoint}
          className="irrigation-tuning-input"
          disabled={!isEditing || isSaving}
          onChange={handleRefillPointChange}
        />
      ) : (
        <span className="irrigation-tuning-readonly-value">
          {values.refillPoint ?? '-'}
        </span>
      ),
      title: TUNING_TOOLTIP?.refillPoint?.title,
    }] : []),
  ], [
    handleKrdiChange,
    handleRefillPointChange,
    handleTargetPointChange,
    isEditing,
    isSaving,
    selectedTargetPoint,
    t,
    values.irrigationTargetPoint,
    values.krdi,
    values.refillPoint,
  ]);

  return (
    <form className="irrigation-recommendation-tuning" noValidate onSubmit={handleSave}>
      {renderTableWithoutHeader(tuningRows)}
      <div className="align-right" style={ACTION_ROW_STYLE}>
        {isEditing ? (
          <>
            <Button
              type="default"
              htmlType="button"
              disabled={isSaving}
              style={CANCEL_BUTTON_STYLE}
              onClick={handleCancelClick}
            >
              {t('Cancel')}
            </Button>
            <Button
              type={hasChanges ? 'primary' : 'default'}
              htmlType="submit"
              loading={isSaving}
              disabled={!hasChanges || isSaving}
            >
              {t('Save')}
            </Button>
          </>
        ) : (
          <Button
            type="default"
            htmlType="button"
            disabled={isSaving}
            icon={<EditOutlined />}
            onClick={handleEditClick}
          >
            {t('Edit')}
          </Button>
        )}
      </div>
    </form>
  );
}

IrrigationRecommendationTuning.propTypes = {
  blockId: PropTypes.number.isRequired,
};
